import { useCallback, useEffect, useRef } from 'react';

import { subscribeKey, useSnapshot } from '@pukka/valtio';
import { IoIosCloseCircle, IoIosArrowDropleftCircle, IoIosArrowDroprightCircle } from 'react-icons/io';

import { LoaderRef } from '../../../Loader';
import { Pill } from '../../../Pill';
import { ImageUploadFileType, ImageUploadState } from '../ImageUpload.state';

import { ImageUploadContainer } from './Container';
import { ImageUploadInputLoader } from './Loader';
import { ImageUploadInputRetrier } from './Retrier';

interface ImageUploadPreviewProps<T> {
  readonly large?: boolean;
  readonly deletable?: boolean;
  readonly ordering?: boolean;
  readonly data: Readonly<ImageUploadFileType<T>>;
  readonly onRemove: ImageUploadState<T>['onRemove'];
  readonly onRetryUpload: ImageUploadState<T>['onRetryUpload'];
  readonly onReorder: ImageUploadState<T>['onReorder'];
  readonly className?: string;
}

function ImageUploadInputPreview<T>({
  className,
  deletable = true,
  ordering,
  data: _data,
  large,
  onRemove,
  onReorder,
  onRetryUpload,
}: ImageUploadPreviewProps<T>) {
  const { nonce, name, url, status } = useSnapshot(_data);
  const progress = useRef<LoaderRef>(null);

  const onClickRemove = useCallback(() => {
    if (deletable) {
      onRemove?.(nonce);
    }
  }, [onRemove, deletable, nonce]);

  const onClickRetry = useCallback(() => {
    if (nonce) {
      onRetryUpload?.(nonce);
    }
  }, [nonce, onRetryUpload]);

  useEffect(() => {
    const unsub = subscribeKey(_data, 'progress', (p) => {
      if (progress.current && p) {
        progress.current.progress = p;
      }
    });

    return unsub;
  }, [_data]);

  return (
    <ImageUploadContainer title={name} className={className}>
      <img src={url} alt={name} className="absolute left-0 top-0 h-full w-full bg-neutral-300 object-cover" />
      <img src={url} alt={name} className="relative h-full w-full rounded object-contain backdrop-blur-sm" />
      {status === 'uploading' ? <ImageUploadInputLoader value={_data.progress} ref={progress} /> : null}
      {status === 'uploaderr' ? <ImageUploadInputRetrier onClick={onClickRetry} /> : null}
      {status !== 'uploading' && status !== 'uploaderr' ? (
        <>
          {large ? <Pill className="absolute left-1 top-1">Main Image</Pill> : null}
          {ordering || deletable ? (
            <div className="row pointer-events-none absolute right-0.5 top-0.5 flex cursor-pointer opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
              {ordering ? (
                <>
                  <IoIosArrowDropleftCircle
                    role="button"
                    data-testid="order-left"
                    size={large ? 24 : 14}
                    onClick={() => onReorder?.(nonce, -1)}
                    className="text-neutral-700"
                  />
                  <IoIosArrowDroprightCircle
                    role="button"
                    data-testid="order-right"
                    size={large ? 24 : 14}
                    onClick={() => onReorder?.(nonce, 1)}
                    className="text-neutral-700"
                  />
                </>
              ) : null}
              {deletable ? (
                <IoIosCloseCircle
                  data-testid="remover"
                  role="button"
                  size={large ? 24 : 14}
                  onClick={onClickRemove}
                  className="text-red-700"
                />
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </ImageUploadContainer>
  );
}

export { ImageUploadInputPreview };
